/**
 * Servicio para manejar el cierre del navegador
 * Basado en HandleCloseBrowser.jslib
 * Guarda datos cuando el usuario intenta cerrar el navegador
 */

import conversationSaverService from './conversationSaverService';
import webSocketService from './webSocketService';

export class BrowserCloseHandler {
  private closureHandled: boolean = false;
  private lastMessageCount: number = 0;
  private debounceTimer: NodeJS.Timeout | null = null;
  private isProcessing: boolean = false;

  /**
   * Inicializar listeners para detectar cierre del navegador
   */
  initialize(): void {
    // Detectar cuando el usuario intenta cerrar o recargar la página.
    // NO mostramos prompt nativo: el guardado vía fetch keepalive en
    // handleClosure() / conversationSaverService completa el POST aunque la
    // página muera, así que el prompt no protege nada y solo entrena al
    // usuario a darle "Volver a cargar" sin leer. Removido 2026-05-27
    // (feedback Bryan: aparecía en cada recarga).
    window.addEventListener('beforeunload', () => {
      this.handleClosure();
    });

    // Detectar cuando la página se oculta o el navegador está a punto de cerrar
    window.addEventListener('pagehide', () => {
      this.handleClosure();
    });

    // Detectar cambios en la visibilidad de la página (minimizado, cambio de pestaña, etc.)
    // NO desconectar el WebSocket solo por ocultar la página - solo guardar datos
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Solo guardar datos, NO desconectar WebSocket
        // El WebSocket se mantendrá conectado y se reconectará automáticamente si se desconecta
        this.handleClosureWithoutDisconnect();
      } else if (document.visibilityState === 'visible') {
        // Resetear el flag si la página vuelve a ser visible
        this.resetClosureHandled();
      }
    });

    // Detectar cuando la ventana recupera el foco (usuario elige quedarse)
    window.addEventListener('focus', () => {
      this.resetClosureHandled();
    });
  }

  /**
   * Manejar el cierre: guardar datos de conversación sin desconectar WebSocket
   * Para cuando la página se oculta pero no se cierra completamente
   */
  private handleClosureWithoutDisconnect(): void {
    // Solo guardar datos, no desconectar WebSocket
    const messages = webSocketService.getChatHistory();
    const currentMessageCount = messages.length;

    // Solo procesar si hay nuevos mensajes
    if (currentMessageCount <= this.lastMessageCount) {
      return;
    }

    // Guardar datos de conversación sin desconectar
    if (messages.length > 0) {
      const finalMessages = messages.map((m) => ({
        role: m.role,
        content: m.content,
        afterInvalidMessage: m.afterInvalidMessage,
      }));
      conversationSaverService.guardarData(finalMessages);
    }

    this.lastMessageCount = currentMessageCount;
  }

  /**
   * Manejar el cierre: guardar datos de conversación.
   * Ejecuta SÍNCRONO (sin debounce) porque durante beforeunload/pagehide
   * el navegador puede matar la página en cualquier momento — un setTimeout
   * de 100ms puede no llegar a ejecutarse. El fetch interno usa keepalive
   * para que el POST se complete incluso si la página muere.
   */
  private handleClosure(): void {
    // Si ya se está procesando, ignorar
    if (this.isProcessing) {
      return;
    }
    this.processClosure();
  }

  /**
   * Procesar el cierre: verificar si hay nuevos mensajes y guardar
   */
  private processClosure(): void {
    // Si ya se manejó el cierre, no hacer nada
    if (this.closureHandled) {
      return;
    }

    // Obtener mensajes actuales
    const messages = webSocketService.getChatHistory();
    const currentMessageCount = messages.length;

    // Solo procesar si hay nuevos mensajes (el número ha aumentado)
    if (currentMessageCount <= this.lastMessageCount) {
      return;
    }

    // Marcar como procesando
    this.isProcessing = true;
    this.closureHandled = true;
    this.lastMessageCount = currentMessageCount;


    // Guardar datos de conversación
    if (messages.length > 0) {
      const finalMessages = messages.map((m) => ({
        role: m.role,
        content: m.content,
        afterInvalidMessage: m.afterInvalidMessage,
      }));
      conversationSaverService.guardarData(finalMessages);
    }

    // Cerrar WebSocket si está abierto
    webSocketService.disconnect();

    // Marcar como no procesando después de un breve delay
    setTimeout(() => {
      this.isProcessing = false;
    }, 500);
  }

  /**
   * Resetear el flag de cierre manejado
   */
  private resetClosureHandled(): void {
    this.closureHandled = false;
    this.isProcessing = false;
    // Actualizar el contador de mensajes cuando la página vuelve a ser visible
    const messages = webSocketService.getChatHistory();
    this.lastMessageCount = messages.length;
  }
}

export const browserCloseHandler = new BrowserCloseHandler();
export default browserCloseHandler;

